// Per-kind artifact renderers — A5.
//
// Each of the 11 artifact kinds (design doc §4.2) maps to a dedicated
// visual treatment so the user can read the right-panel stack at a
// glance. Simulations get a distinct border + "SIM" badge per §11.3
// — the auditability discipline made visible.

import type { ArtifactView } from "../api/analystClient";
import { formatCell, formatNumber, kindLabel } from "./format";

export interface ArtifactCardProps {
  artifact: ArtifactView;
  /** Click handler for chip / suggestion lines. Used by `followups`
   *  and `clarification` to round-trip a chosen prompt back to the
   *  dialogue panel as the next user message. */
  onChipClick?: (text: string) => void;
}

export function ArtifactCard({ artifact, onChipClick }: ArtifactCardProps) {
  const isSim = artifact.kind === "simulation";
  const isRefusal = artifact.kind === "refusal";
  const border = isSim
    ? "2px dashed #b86b00"
    : isRefusal
      ? "2px solid #b04040"
      : "1px solid #d8d8d8";
  return (
    <div data-testid={`artifact-${artifact.id}`} style={{ ...cardStyle, border }}>
      <header style={cardHeaderStyle}>
        <span style={kindBadgeStyle(artifact.kind)}>{kindLabel(artifact.kind)}</span>
        {isSim && <span style={simBadgeStyle}>SIM</span>}
        {artifact.pinned && <span style={pinBadgeStyle}>📌</span>}
        <code style={idStyle}>{artifact.id}</code>
      </header>
      {renderBody(artifact, onChipClick)}
      <Provenance provenance={artifact.provenance} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Per-kind body renderers
// ─────────────────────────────────────────────────────────────────────


function renderBody(
  art: ArtifactView,
  onChipClick: ((text: string) => void) | undefined,
) {
  const p = art.payload as Record<string, unknown>;
  switch (art.kind) {
    case "frame":
      return <FrameBody payload={p} />;
    case "simulation":
      return <SimulationBody payload={p} />;
    case "narration":
      return <NarrationBody payload={p} />;
    case "followups":
      return <FollowupsBody payload={p} onChipClick={onChipClick} />;
    case "refusal":
      return <RefusalBody payload={p} />;
    case "clarification":
      return <ClarificationBody payload={p} onChipClick={onChipClick} />;
    case "qm_card":
      return <QMCardBody payload={p} />;
    case "structure":
      return <StructureBody payload={p} />;
    case "comparison":
      return <ComparisonBody payload={p} />;
    case "chart":
    case "provenance":
    default:
      return <FallbackBody payload={p} />;
  }
}


function FrameBody({ payload }: { payload: Record<string, unknown> }) {
  const columns = (payload.columns as string[]) ?? [];
  const rows = (payload.rows as unknown[][]) ?? [];
  const rowCount = (payload.row_count as number) ?? rows.length;
  const servedFrom = payload.served_from as string | undefined;
  const veriflevel = payload.verification_level as string | undefined;
  const sliced = rows.slice(0, 50);
  return (
    <>
      <div style={metaRowStyle}>
        <span><strong>{rowCount}</strong> rows × <strong>{columns.length}</strong> cols</span>
        {servedFrom && <span style={badgeStyle(servedFrom)}>{servedFrom}</span>}
        {veriflevel && <span style={verifBadgeStyle(veriflevel)}>{veriflevel}</span>}
      </div>
      <div style={{ overflowX: "auto", maxWidth: "100%" }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c} style={thStyle}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sliced.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td key={j} style={tdStyle}>{formatCell(cell)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 50 && (
        <div style={mutedStyle}>… {rows.length - 50} more rows omitted from view</div>
      )}
    </>
  );
}


function SimulationBody({ payload }: { payload: Record<string, unknown> }) {
  const simKind = payload.simulation_kind as string | undefined;
  if (simKind === "goal_seek") {
    const col = payload.column as string;
    const cur = payload.current_total as number;
    const tgt = payload.target_value as number;
    const pct = payload.required_delta_pct as number;
    const vary = payload.vary as string;
    const varyDelta = payload.vary_delta as number | null;
    return (
      <div>
        <div style={metaRowStyle}>
          <span><strong>goal-seek</strong> on <code>{col}</code></span>
        </div>
        <table style={kvTableStyle}>
          <tbody>
            <tr><td style={kvLabel}>current</td><td>{formatNumber(cur)}</td></tr>
            <tr><td style={kvLabel}>target</td><td>{formatNumber(tgt)}</td></tr>
            <tr><td style={kvLabel}>delta</td><td>{pct.toFixed(2)}%</td></tr>
            {varyDelta !== null && varyDelta !== undefined && (
              <tr><td style={kvLabel}>{vary} must change by</td><td>{formatNumber(varyDelta)}</td></tr>
            )}
          </tbody>
        </table>
        <div style={mutedStyle}>{payload.assumption as string}</div>
      </div>
    );
  }
  // filter + perturbation render like a Frame.
  return (
    <>
      <div style={metaRowStyle}>
        <span><strong>{simKind ?? "simulation"}</strong></span>
        {payload.filter_added != null && (
          <code>WHERE {String(payload.filter_added)}</code>
        )}
      </div>
      <FrameBody payload={payload} />
    </>
  );
}


function NarrationBody({ payload }: { payload: Record<string, unknown> }) {
  return <p style={narrationStyle}>{payload.text as string}</p>;
}


function FollowupsBody({
  payload,
  onChipClick,
}: {
  payload: Record<string, unknown>;
  onChipClick: ((text: string) => void) | undefined;
}) {
  const items = (payload.followups as string[]) ?? [];
  return (
    <div>
      <div style={mutedStyle}>Follow-up suggestions</div>
      <div style={chipsRowStyle}>
        {items.map((text, i) => (
          <button
            key={i}
            type="button"
            style={chipStyle}
            onClick={() => onChipClick?.(text)}
            disabled={!onChipClick}
          >
            {text}
          </button>
        ))}
      </div>
    </div>
  );
}


function RefusalBody({ payload }: { payload: Record<string, unknown> }) {
  return (
    <div>
      <div style={{ ...mutedStyle, color: "#b04040" }}>
        <strong>Refused:</strong> {String(payload.reason ?? payload.refusal_kind ?? "")}
      </div>
      {payload.suggestion != null && (
        <div>Try: {String(payload.suggestion)}</div>
      )}
    </div>
  );
}


function ClarificationBody({
  payload,
  onChipClick,
}: {
  payload: Record<string, unknown>;
  onChipClick: ((text: string) => void) | undefined;
}) {
  const question = (payload.question as string) ?? "(no question)";
  const options = (payload.options as string[]) ?? [];
  return (
    <div>
      <p>{question}</p>
      <div style={chipsRowStyle}>
        {options.map((opt, i) => (
          <button
            key={i}
            type="button"
            style={chipStyle}
            onClick={() => onChipClick?.(opt)}
            disabled={!onChipClick}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}


function QMCardBody({ payload }: { payload: Record<string, unknown> }) {
  const col = payload.name ?? payload.column ?? "?";
  return (
    <table style={kvTableStyle}>
      <tbody>
        <tr><td style={kvLabel}>column</td><td>{String(col)}</td></tr>
        {payload.dtype != null && (
          <tr><td style={kvLabel}>dtype</td><td>{String(payload.dtype)}</td></tr>
        )}
        {payload.nunique != null && (
          <tr><td style={kvLabel}>nunique</td><td>{String(payload.nunique)}</td></tr>
        )}
        {payload.n_rows != null && (
          <tr><td style={kvLabel}>n_rows</td><td>{String(payload.n_rows)}</td></tr>
        )}
        {payload.null_rate != null && (
          <tr><td style={kvLabel}>null_rate</td><td>{String(payload.null_rate)}</td></tr>
        )}
        {payload.cardinality_class != null && (
          <tr><td style={kvLabel}>cardinality</td><td>{String(payload.cardinality_class)}</td></tr>
        )}
        {Array.isArray(payload.top_values) && (
          <tr>
            <td style={kvLabel}>top</td>
            <td>
              {(payload.top_values as Array<[unknown, number]>)
                .slice(0, 5)
                .map(([v, c], i) => (
                  <div key={i}>
                    <code>{String(v)}</code> · {c}
                  </div>
                ))}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}


function StructureBody({ payload }: { payload: Record<string, unknown> }) {
  const schema = payload.schema as string | undefined;
  const edges = (payload.reduced_edges as Array<{
    determinant: string[];
    dependent: string;
  }>) ?? [];
  const keys = (payload.single_column_keys as string[]) ?? [];
  return (
    <div>
      {schema && <div style={metaRowStyle}><code>{schema}</code></div>}
      {keys.length > 0 && (
        <div style={mutedStyle}>keys: {keys.join(", ")}</div>
      )}
      {edges.length > 0 && (
        <ul style={listStyle}>
          {edges.slice(0, 10).map((e, i) => (
            <li key={i}>
              <code>{e.determinant.join(", ")} → {e.dependent}</code>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function ComparisonBody({ payload }: { payload: Record<string, unknown> }) {
  return (
    <FallbackBody payload={payload} />
  );
}


function FallbackBody({ payload }: { payload: Record<string, unknown> }) {
  return (
    <pre style={preStyle}>{JSON.stringify(payload, null, 2)}</pre>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Provenance footer (§4.3 — the auditability commitment)
// ─────────────────────────────────────────────────────────────────────


function Provenance({ provenance }: { provenance: Record<string, unknown> }) {
  if (!provenance || Object.keys(provenance).length === 0) return null;
  return (
    <details style={provenanceStyle}>
      <summary style={mutedStyle}>provenance</summary>
      <pre style={preStyle}>{JSON.stringify(provenance, null, 2)}</pre>
    </details>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────────
// Inline styles (matches the existing pages' style approach — keeps
// the slice self-contained, no new CSS pipeline).
// ─────────────────────────────────────────────────────────────────────


const cardStyle: React.CSSProperties = {
  background: "#fff",
  borderRadius: 8,
  padding: "12px 14px",
  marginBottom: 12,
  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
};


const cardHeaderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 8,
  fontSize: 12,
  flexWrap: "wrap",
};


function kindBadgeStyle(kind: string): React.CSSProperties {
  const palette: Record<string, string> = {
    frame: "#3b82f6",
    narration: "#10b981",
    simulation: "#b86b00",
    refusal: "#b04040",
    clarification: "#9333ea",
    followups: "#06b6d4",
    qm_card: "#64748b",
    structure: "#475569",
  };
  return {
    background: palette[kind] ?? "#64748b",
    color: "#fff",
    padding: "2px 8px",
    borderRadius: 999,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    fontSize: 10,
  };
}


const simBadgeStyle: React.CSSProperties = {
  background: "#fff5e6",
  color: "#b86b00",
  border: "1px solid #b86b00",
  padding: "1px 6px",
  borderRadius: 4,
  fontWeight: 700,
  letterSpacing: 0.5,
  fontSize: 10,
};


const pinBadgeStyle: React.CSSProperties = {
  fontSize: 14,
};


const idStyle: React.CSSProperties = {
  marginLeft: "auto",
  color: "#94a3b8",
  fontSize: 10,
};


const metaRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  marginBottom: 6,
  fontSize: 12,
};


function badgeStyle(label: string): React.CSSProperties {
  const isCache = label === "engine_cache";
  return {
    background: isCache ? "#e0f2fe" : "#f1f5f9",
    color: isCache ? "#075985" : "#475569",
    border: `1px solid ${isCache ? "#bae6fd" : "#cbd5e1"}`,
    padding: "1px 6px",
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 600,
  };
}


function verifBadgeStyle(level: string): React.CSSProperties {
  const colour =
    level === "AAA" ? "#065f46" :
    level === "AA" ? "#92400e" :
    level === "A" ? "#9a3412" : "#475569";
  return {
    background: "#fff",
    border: `1px solid ${colour}`,
    color: colour,
    padding: "1px 6px",
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 700,
  };
}


const tableStyle: React.CSSProperties = {
  borderCollapse: "collapse",
  fontSize: 12,
  width: "100%",
};


const thStyle: React.CSSProperties = {
  borderBottom: "1px solid #cbd5e1",
  textAlign: "left",
  padding: "4px 8px",
  background: "#f8fafc",
  fontWeight: 600,
};


const tdStyle: React.CSSProperties = {
  borderBottom: "1px solid #f1f5f9",
  padding: "4px 8px",
};


const kvTableStyle: React.CSSProperties = {
  borderCollapse: "collapse",
  fontSize: 12,
};


const kvLabel: React.CSSProperties = {
  color: "#64748b",
  paddingRight: 12,
  textAlign: "right",
  verticalAlign: "top",
};


const narrationStyle: React.CSSProperties = {
  fontSize: 14,
  lineHeight: 1.5,
  margin: 0,
};


const chipsRowStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
  marginTop: 6,
};


const chipStyle: React.CSSProperties = {
  background: "#f0f9ff",
  border: "1px solid #bae6fd",
  color: "#075985",
  padding: "6px 10px",
  borderRadius: 16,
  cursor: "pointer",
  fontSize: 12,
};


const mutedStyle: React.CSSProperties = {
  color: "#64748b",
  fontSize: 12,
};


const provenanceStyle: React.CSSProperties = {
  marginTop: 8,
  fontSize: 11,
};


const preStyle: React.CSSProperties = {
  background: "#f8fafc",
  padding: 8,
  borderRadius: 4,
  margin: 0,
  overflow: "auto",
  fontSize: 11,
};


const listStyle: React.CSSProperties = {
  margin: "4px 0 0 0",
  paddingLeft: 16,
  fontSize: 12,
};
